import { useCallback, useEffect, useState } from "react";
import {
  Car, CheckCircle2, Eye, EyeOff, Loader2, LogOut, Mail, Phone, Star, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { AdminArticles } from "../components/AdminArticles";

const STATUS_LABELS = {
  pending_payment: ["Paiement en attente", "bg-amber-100 text-amber-800"],
  confirmed: ["Confirmée", "bg-azure-soft text-azure"],
  in_progress: ["En cours", "bg-indigo-100 text-indigo-700"],
  completed: ["Terminée", "bg-emerald-100 text-emerald-700"],
  cancelled: ["Annulée", "bg-slate-100 text-slate-500"],
};

const Stars = ({ value }) => (
  <span className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={13} className={i <= value ? "fill-amber-400 text-amber-400" : "text-slate-300"} />
    ))}
  </span>
);

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem("cabnow_admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(!!token);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [exportMonth, setExportMonth] = useState(new Date().toISOString().slice(0, 7));

  const exportPdf = async () => {
    try {
      const r = await api.get(`/admin/exports/monthly?month=${exportMonth}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cabnow-releve-${exportMonth}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Relevé PDF téléchargé.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const loadAll = useCallback(async () => {
    try {
      const [b, r, q, s] = await Promise.all([
        api.get("/admin/bookings"), api.get("/admin/reviews"),
        api.get("/admin/quote-requests"), api.get("/admin/stats"),
      ]);
      setBookings(b.data);
      setReviews(r.data);
      setQuotes(q.data);
      setStats(s.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("cabnow_admin_token");
        setToken("");
        setAuthed(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!token) { setChecking(false); return; }
    api.get("/auth/me")
      .then(() => { setAuthed(true); loadAll(); })
      .catch(() => { localStorage.removeItem("cabnow_admin_token"); setToken(""); })
      .finally(() => setChecking(false));
  }, [token, loadAll]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("cabnow_admin_token", data.token);
      setToken(data.token);
    } catch (err) {
      setLoginError(formatApiError(err));
    }
  };

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.post(`/admin/bookings/${id}/status`, { status });
      if (status === "completed") {
        toast.success(data.review_email_sent
          ? "Course terminée — email de demande d'avis envoyé au client."
          : "Course terminée.");
      } else {
        toast.success("Statut mis à jour.");
      }
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleReview = async (id) => {
    await api.post(`/admin/reviews/${id}/toggle`).catch(() => {});
    loadAll();
  };

  const deleteReview = async (id) => {
    await api.delete(`/admin/reviews/${id}`).catch(() => {});
    toast.success("Avis supprimé.");
    loadAll();
  };

  if (checking) {
    return <div className="flex min-h-screen items-center justify-center bg-sand"><Loader2 size={32} className="animate-spin text-azure" /></div>;
  }

  if (!authed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy px-4" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl bg-white p-10 shadow-2xl">
          <div className="mb-8 flex justify-center"><Logo /></div>
          <h1 className="text-center font-serif text-2xl font-semibold text-navy">Espace chauffeur</h1>
          <p className="mt-1 text-center text-xs text-navy/50">Gestion des courses CabNow</p>
          <label className="mb-1.5 mt-8 block text-xs font-semibold uppercase tracking-wider text-navy/50">Email</label>
          <input data-testid="admin-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-azure focus:ring-2 focus:ring-azure/15" />
          <label className="mb-1.5 mt-4 block text-xs font-semibold uppercase tracking-wider text-navy/50">Mot de passe</label>
          <input data-testid="admin-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-azure focus:ring-2 focus:ring-azure/15" />
          {loginError && <p className="mt-3 text-sm font-medium text-red-600" data-testid="admin-login-error">{loginError}</p>}
          <button data-testid="admin-login-button" type="submit"
            className="mt-6 w-full rounded-full bg-azure py-3.5 text-sm font-bold text-white transition-colors hover:bg-azure-hover">
            Se connecter
          </button>
        </form>
      </div>
    );
  }

  const TABS = [
    ["bookings", `Réservations (${bookings.length})`],
    ["reviews", `Avis (${reviews.length})`],
    ["quotes", `Devis (${quotes.length})`],
    ["articles", "Articles"],
  ];

  return (
    <div className="min-h-screen bg-sand" data-testid="admin-dashboard">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Logo />
          <button data-testid="admin-logout-button"
            onClick={() => { localStorage.removeItem("cabnow_admin_token"); setToken(""); setAuthed(false); }}
            className="flex items-center gap-2 text-sm font-semibold text-navy/60 hover:text-red-600">
            <LogOut size={15} /> Déconnexion
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        {stats && (
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="admin-stats">
            {[
              ["Courses actives", stats.bookings_active],
              ["Chiffre d'affaires", `${stats.revenue_eur} €`],
              ["Note moyenne", stats.reviews_count ? `${String(stats.reviews_average).replace(".", ",")}/5` : "—"],
              ["Devis en attente", stats.quotes_pending],
            ].map(([label, value]) => (
              <div key={label} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <p className="text-xs font-semibold uppercase tracking-wider text-navy/45">{label}</p>
                <p className="mt-2 font-serif text-3xl font-semibold text-navy">{value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="admin-tabs">
          {TABS.map(([key, label]) => (
            <button key={key} data-testid={`admin-tab-${key}`} onClick={() => setTab(key)}
              className={`rounded-full px-5 py-2.5 text-sm font-bold transition-colors ${
                tab === key ? "bg-navy text-white" : "bg-white text-navy/60 hover:bg-slate-100"
              }`}>
              {label}
            </button>
          ))}
          <span className="ml-auto flex items-center gap-2 rounded-full bg-white px-3 py-1.5 shadow-sm">
            <input
              type="month"
              data-testid="admin-export-month"
              value={exportMonth}
              onChange={(e) => setExportMonth(e.target.value)}
              className="text-sm font-semibold text-navy outline-none"
            />
            <button
              data-testid="admin-export-pdf-button"
              onClick={exportPdf}
              className="rounded-full bg-gold px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-gold/90"
            >
              Relevé PDF
            </button>
          </span>
        </div>

        {tab === "bookings" && (
          <div className="mt-6 space-y-4" data-testid="admin-bookings-list">
            {bookings.length === 0 && <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-navy/50">Aucune réservation pour le moment.</p>}
            {bookings.map((b) => {
              const [label, cls] = STATUS_LABELS[b.status] || [b.status, "bg-slate-100 text-slate-600"];
              return (
                <div key={b.id} data-testid={`admin-booking-${b.id.slice(0, 8)}`}
                  className="flex flex-col gap-4 rounded-2xl border border-slate-100 bg-white p-6 shadow-sm lg:flex-row lg:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-xs font-bold text-navy/40">{b.id.slice(0, 8).toUpperCase()}</span>
                      <span className={`rounded-full px-3 py-1 text-xs font-bold ${cls}`}>{label}</span>
                      <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-navy/60">
                        {b.payment_method === "online"
                          ? (b.payment_status === "paid" ? "Payé en ligne" : "En ligne — non payé")
                          : b.payment_method === "onboard_cash" ? "Espèces à bord" : "CB à bord"}
                      </span>
                      {b.voucher_no && (
                        <span className="rounded-full bg-gold-light px-3 py-1 font-mono text-xs font-bold text-gold">{b.voucher_no}</span>
                      )}
                    </div>
                    <p className="mt-2 font-serif text-lg font-semibold text-navy">{b.pickup_label} → {b.dropoff_label}</p>
                    {(b.pickup_address || b.dropoff_address) && (
                      <p className="mt-1 text-xs font-medium text-navy/55" data-testid={`admin-addresses-${b.id.slice(0, 8)}`}>
                        {b.pickup_address && <span>Prise en charge : {b.pickup_address}</span>}
                        {b.pickup_address && b.dropoff_address && " · "}
                        {b.dropoff_address && <span>Destination : {b.dropoff_address}</span>}
                      </p>
                    )}
                    <p className="mt-1 text-sm text-navy/55">
                      {b.date} à {b.time} · {b.vehicle === "berline" ? "Berline" : "Van"} · {b.passengers} pers. · <strong>{b.price_eur} €</strong>
                    </p>
                    <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-navy/50">
                      <span className="font-semibold text-navy/70">{b.name}</span>
                      <a href={`tel:${b.phone}`} className="flex items-center gap-1 hover:text-azure"><Phone size={11} /> {b.phone}</a>
                      <a href={`mailto:${b.email}`} className="flex items-center gap-1 hover:text-azure"><Mail size={11} /> {b.email}</a>
                      {b.flight_number && <span>Vol/train : {b.flight_number}</span>}
                      {b.child_seat && (
                        <span data-testid={`admin-seat-${b.id.slice(0, 8)}`}>
                          Siège : {{ "0-2": "coque bébé (0-2 ans)", "3-7": "siège enfant (3-7 ans)", "7+": "réhausseur (7+ ans)" }[b.child_seat_age] || "oui"}
                        </span>
                      )}
                    </p>
                    {b.notes && <p className="mt-1 text-xs italic text-navy/45">« {b.notes} »</p>}
                  </div>
                  <div className="flex shrink-0 gap-2">
                    {(b.status === "confirmed" || b.status === "in_progress") && (
                      <button data-testid={`admin-complete-${b.id.slice(0, 8)}`} onClick={() => setStatus(b.id, "completed")}
                        className="flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-emerald-600">
                        <CheckCircle2 size={13} /> Terminer + avis
                      </button>
                    )}
                    {b.status === "confirmed" && (
                      <button data-testid={`admin-start-${b.id.slice(0, 8)}`} onClick={() => setStatus(b.id, "in_progress")}
                        className="flex items-center gap-1.5 rounded-full bg-indigo-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-indigo-600">
                        <Car size={13} /> En course
                      </button>
                    )}
                    {(b.status === "confirmed" || b.status === "pending_payment") && (
                      <button data-testid={`admin-cancel-${b.id.slice(0, 8)}`} onClick={() => setStatus(b.id, "cancelled")}
                        className="rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-navy/60 transition-colors hover:bg-slate-50">
                        Annuler
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "articles" && <AdminArticles />}

        {tab === "reviews" && (
          <div className="mt-6 grid gap-4 md:grid-cols-2" data-testid="admin-reviews-list">
            {reviews.length === 0 && <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-navy/50 md:col-span-2">Aucun avis pour le moment.</p>}
            {reviews.map((r) => (
              <div key={r.id} data-testid={`admin-review-${r.id.slice(0, 8)}`} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <Stars value={r.rating} />
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${r.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {r.published ? "Publié" : "Masqué"}
                  </span>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-navy/70">« {r.comment} »</p>
                <p className="mt-2 text-xs text-navy/50">{r.author_name} · {r.route}</p>
                <div className="mt-4 flex gap-2">
                  <button data-testid={`admin-review-toggle-${r.id.slice(0, 8)}`} onClick={() => toggleReview(r.id)}
                    className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-navy/60 hover:bg-slate-50">
                    {r.published ? <><EyeOff size={13} /> Masquer</> : <><Eye size={13} /> Publier</>}
                  </button>
                  <button data-testid={`admin-review-delete-${r.id.slice(0, 8)}`} onClick={() => deleteReview(r.id)}
                    className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-500 hover:bg-red-50">
                    <Trash2 size={13} /> Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "quotes" && (
          <div className="mt-6 space-y-4" data-testid="admin-quotes-list">
            {quotes.length === 0 && <p className="rounded-2xl border border-dashed border-slate-300 p-10 text-center text-sm text-navy/50">Aucune demande de devis.</p>}
            {quotes.map((q) => (
              <div key={q.id} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm" data-testid={`admin-quote-${q.id.slice(0, 8)}`}>
                <p className="font-serif text-lg font-semibold text-navy">{q.pickup_address} → {q.dropoff_address}</p>
                <p className="mt-1 text-sm text-navy/55">{q.date} à {q.time} · {q.passengers} passager(s)</p>
                <p className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-navy/50">
                  <span className="font-semibold text-navy/70">{q.name}</span>
                  <a href={`tel:${q.phone}`} className="flex items-center gap-1 hover:text-azure"><Phone size={11} /> {q.phone}</a>
                  <a href={`mailto:${q.email}`} className="flex items-center gap-1 hover:text-azure"><Mail size={11} /> {q.email}</a>
                </p>
                {q.notes && <p className="mt-1 text-xs italic text-navy/45">« {q.notes} »</p>}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
